import { createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { client } from 'src/graphql/client';
import { GET_ROUTING_IMPORT_PLAN_COMPARISON } from 'src/graphql/requests/getRoutingImportPlanComparison';
import {
  GGetRoutingImportPlanComparisonQuery,
  GRoutificRunStatus,
} from 'src/graphql/types/generated';
import { RootState } from 'src/state/store';
import { DeliveryState } from './state';
import { getRoutes } from './getRoutes';

const ACTIVE_STATUSES: GRoutificRunStatus[] = [
  GRoutificRunStatus.Pending,
  GRoutificRunStatus.Processing,
];

// True while the selected warehouse + night has a manual Routific run in progress. Drives the
// route/stop edit lock (via isRoutingLockedSelector) — the manual-run counterpart of
// routeImport.isRunning for automated routing.
export const getManualRunStatus = createAsyncThunk(
  'delivery/getManualRunStatus',
  async (
    { viewingNightEnd }: { viewingNightEnd: string },
    thunkAPI,
  ): Promise<boolean> => {
    const { delivery }: RootState = thunkAPI.getState() as RootState;
    const warehouseId = delivery.selectedWarehouseId;
    if (!warehouseId || !viewingNightEnd) return false;

    const { data, error } = await client
      .query<GGetRoutingImportPlanComparisonQuery>(
        GET_ROUTING_IMPORT_PLAN_COMPARISON,
        { input: { warehouseId, nightEnd: viewingNightEnd } },
        { requestPolicy: 'network-only' },
      )
      .toPromise();

    if (error) return false;

    const runs = data?.routingImportPlan?.manualRoutificRuns ?? [];
    const isRunning = runs.some((run) => ACTIVE_STATUSES.includes(run.status));

    // When a run just finished, refresh the routes/stops so the DMP reflects the new order +
    // estimates (mirrors getRouteImportInfo's refresh on automated-routing completion).
    if (delivery.manualRunRunning && !isRunning) {
      thunkAPI.dispatch(getRoutes({ warehouseId, nightEnd: viewingNightEnd }));
    }

    return isRunning;
  },
);

export const getManualRunStatusFulfilled = (
  state: DeliveryState,
  action: PayloadAction<boolean>,
): void => {
  state.manualRunRunning = action.payload;
};
